import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import { Avatar } from '@/components/Avatar';
import { Icon } from '@/components/Icon';
import { StatusPill } from '@/components/StatusPill';
import { EmptyState } from '@/components/EmptyState';
import { haptic } from '@/lib/telegram';
import type { AccountStatus } from '@/types/domain';

const FILTERS: { key: AccountStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'все' },
  { key: 'active', label: 'активные' },
  { key: 'cooldown', label: 'cooldown' },
  { key: 'banned', label: 'забанены' },
];

export function AccountsPage(): JSX.Element {
  const { data, loading } = useAsync(() => api.listAccounts(), []);
  const [filter, setFilter] = useState<AccountStatus | 'all'>('all');
  const [open, setOpen] = useState(false);

  const filtered = useMemo(
    () => (data ?? []).filter((account) => filter === 'all' || account.status === filter),
    [data, filter],
  );

  return (
    <div className="space-y-4 pb-8">
      <header className="flex items-end justify-between pt-1">
        <div>
          <p className="text-[12px] uppercase tracking-[0.22em] text-[#747c9f]">парк аккаунтов</p>
          <h1 className="mt-1 text-[26px] font-bold text-white">{data?.length ?? '…'} аккаунтов</h1>
        </div>
        <button
          onClick={() => {
            haptic('medium');
            setOpen(true);
          }}
          className="flex h-14 w-14 items-center justify-center rounded-[20px] bg-[#6077ff] text-white shadow-[0_16px_28px_rgba(78,101,255,0.34)] active:scale-95"
        >
          <Icon name="plus" />
        </button>
      </header>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {FILTERS.map((item) => (
          <button
            key={item.key}
            onClick={() => {
              haptic('light');
              setFilter(item.key);
            }}
            className={`rounded-full border px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.12em] transition ${
              filter === item.key
                ? 'border-[#435bff] bg-[#121c49] text-[#c9d3ff]'
                : 'border-white/10 bg-white/4 text-[#848cab]'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="space-y-3">
          <div className="skeleton h-20" />
          <div className="skeleton h-20" />
          <div className="skeleton h-20" />
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <EmptyState
          icon="users"
          title="Нет аккаунтов в этом фильтре"
          body="Добавьте новые аккаунты через импорт сессий или мастер входа по телефону."
          cta={<button className="btn-primary" onClick={() => setOpen(true)}>Добавить аккаунт</button>}
        />
      )}

      <ul className="space-y-3">
        {filtered.map((account) => (
          <li key={account.id}>
            <Link
              to={`/accounts/${account.id}`}
              onClick={() => haptic('light')}
              className="card flex items-center gap-3 p-4 transition active:scale-[0.99]"
            >
              <Avatar name={account.name || account.username || `acc${account.id}`} size={54} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="truncate text-[16px] font-semibold text-white">
                    {account.name || account.username || `Account ${account.id}`}
                  </div>
                  <StatusPill status={account.status} />
                </div>
                <div className="mt-1 truncate text-[13px] text-[#7f87aa]">
                  {account.username ? `@${account.username.replace(/^@/, '')}` : 'без username'}
                  {account.proxy ? ` · ${account.proxy.ip}:${account.proxy.port}` : ''}
                </div>
              </div>
              <span className="text-[#70789d]">
                <Icon name="chevron-right" />
              </span>
            </Link>
          </li>
        ))}
      </ul>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/65 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-t-[32px] border border-white/10 bg-[#0c1020] p-5"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mx-auto mb-3 h-1 w-12 rounded-full bg-white/10" />
            <div className="mb-3 text-[17px] font-semibold text-white">Добавить аккаунт</div>
            <div className="grid gap-2">
              <button className="card flex items-center gap-3 p-4 text-left active:scale-[0.99]">
                <span className="icon-circle-ghost"><Icon name="upload" /></span>
                <div>
                  <div className="text-[15px] font-semibold text-white">Импорт sessionString</div>
                  <div className="text-[13px] text-[#7f87aa]">массово, по строкам с прокси</div>
                </div>
              </button>
              <button className="card flex items-center gap-3 p-4 text-left active:scale-[0.99]">
                <span className="icon-circle-ghost"><Icon name="users" /></span>
                <div>
                  <div className="text-[15px] font-semibold text-white">Добавить по номеру</div>
                  <div className="text-[13px] text-[#7f87aa]">телефон → SMS → 2FA</div>
                </div>
              </button>
            </div>
            <button className="btn-ghost mt-4 w-full" onClick={() => setOpen(false)}>Закрыть</button>
          </div>
        </div>
      )}
    </div>
  );
}
